export type CsvColumn<T> = {
  key: string;
  header: string;
  get: (row: T) => unknown;
};

// A spreadsheet runs a cell that opens with =, +, -, @, a tab or a carriage return as a formula (CWE-1236), and
// contact names and call answers are text other people wrote: such a cell gets an apostrophe in front (OWASP).
// A number or a phone number keeps its own + or -, as in the Call History export (utils/call-export.ts):
// mangling the phone_number column would be a worse bug than the one fixed.
const NUMERIC_LOOKING = /^[+-]?[\d\s()./-]+$/;
const defuseFormula = (s: string): string =>
  /^[=@\t\r]/.test(s) || (/^[+-]/.test(s) && !NUMERIC_LOOKING.test(s)) ? `'${s}` : s;

const escapeCell = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  let s: string;
  if (typeof value === "boolean") s = value ? "true" : "false";
  else if (value instanceof Date) s = value.toISOString();
  else if (typeof value === "object") s = JSON.stringify(value);
  else s = defuseFormula(String(value));
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

export const toCsv = <T,>(rows: T[], columns: CsvColumn<T>[]): string => {
  const header = columns.map((c) => escapeCell(c.header)).join(",");
  const body = rows
    .map((row) => columns.map((c) => escapeCell(c.get(row))).join(","))
    .join("\n");
  return body ? `${header}\n${body}\n` : `${header}\n`;
};

export const downloadCsv = (filename: string, csv: string) => {
  const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
